import React, { useEffect, useState } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import classnames from 'classnames/bind';
import { ChevronLeft, ChevronRight, Menu, X } from 'lucide-react';

import styles from './SidebarToggleButton.module.scss';

const cx: CX = classnames.bind(styles);

function SidebarToggleButton({
    isCollapsed,
    onClick,
    variant,
    size,
    position,
    tooltip,
    className,
}: SidebarToggleButtonProps) {
    const [showAttention, setShowAttention] = useState(false);

    // Attention animation: start 1 second after mount, last 1 second
    useEffect(() => {
        const startTimer = setTimeout(() => {
            setShowAttention(true);
            
            // Remove attention animation after 1 second
            const endTimer = setTimeout(() => {
                setShowAttention(false);
            }, 1000);

            return () => clearTimeout(endTimer);
        }, 1000);

        return () => clearTimeout(startTimer);
    }, []);

    const renderIcon = () => {
        if (variant === 'hamburger') {
            return isCollapsed ? <Menu size={20} /> : <X size={20} />;
        }
        
        // When sidebar is closed (collapsed), show right arrow to indicate "open"
        // When sidebar is open (expanded), show left arrow to indicate "close"
        return isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />;
    };

    const getTooltipText = () => {
        if (tooltip) return tooltip;
        return isCollapsed ? 'Open sidebar' : 'Close sidebar';
    };

    return (
        <button
            type="button"
            className={cx('sidebar-toggle-button', {
                collapsed: isCollapsed,
                expanded: !isCollapsed,
                [variant as string]: true,
                [size as string]: true,
                [position as string]: true,
                'show-attention': showAttention,
            }, className)}
            onClick={onClick}
            title={getTooltipText()}
            aria-label={getTooltipText()}
        >
            <div className={cx('button-content')}>
                <div className={cx('icon-wrapper')}>
                    {renderIcon()}
                </div>
                {/* Hover tooltip */}
                <div className={cx('tooltip')}>
                    {getTooltipText()}
                </div>
                {/* Ripple effect container */}
                <div className={cx('ripple')} />
            </div>
        </button>
    );
}

SidebarToggleButton.defaultProps = {
    variant: 'chevron',
    size: 'medium',
    position: 'fixed',
    tooltip: undefined,
    className: undefined,
};

SidebarToggleButton.propTypes = {
    isCollapsed: PropTypes.bool.isRequired,
    onClick: PropTypes.func.isRequired,
    variant: PropTypes.oneOf(['chevron', 'hamburger']),
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    position: PropTypes.oneOf(['fixed', 'relative']),
    tooltip: PropTypes.string,
    className: PropTypes.string,
};

type SidebarToggleButtonProps = InferProps<typeof SidebarToggleButton.propTypes>;

export default SidebarToggleButton; 